import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { effect, inject, DOCUMENT } from '@angular/core';
import { ThemeService } from '@theme/data-access';
import { LocalStorageFacade } from '@application/localstorage';
import { debounceTime, fromEvent } from 'rxjs';
import { RouterStore } from '@route/data-access';

/**
 * Tailwind's own `sm` boundary — the last width below the `sm:` prefix.
 *
 * The CDK/Material breakpoints cut at 600/960, so `isMobile` (Small ∪ XSmall)
 * can't stand in for a `sm:` / `max-sm:` class. Observed through the same
 * `BreakpointObserver`, this custom query keeps `isBelowTailwindSm` on the
 * exact 640px line those classes use.
 */
const BELOW_SM_QUERY = '(max-width: 639.98px)';

export interface IUiStore {
  userClickEventTarget: any | null;
  theme: {
    mode: 'light' | 'dark' | 'system';
    dark: boolean;
  };
  screen: {
    breakpoint?:
    | 'Unknown'
    | 'XSmall'
    | 'Small'
    | 'Medium'
    | 'Large'
    | 'XLarge'
    | string;
    innerWidth?: number | null;
    innerHeight?: number | null;
    orientation?: 'landscape' | 'portrait' | string | null;
  };
  opacityScreen?: boolean;
  hideMaxWidth?: boolean;
  isPrinting: boolean;
  /** Viewport below Tailwind's `sm` breakpoint — agrees with `sm:` / `max-sm:`. */
  isBelowTailwindSm: boolean;
}

export const UiInitialStore: IUiStore = {
  userClickEventTarget: null,
  theme: { mode: 'system', dark: false },
  screen: {
    breakpoint: 'Unknown',
    innerWidth: null,
    innerHeight: null,
    orientation: null,
  },
  opacityScreen: false,
  hideMaxWidth: false,
  isPrinting: false,
  isBelowTailwindSm: false,
};

const displayNameMap = new Map([
  [Breakpoints.XSmall, 'XSmall'],
  [Breakpoints.Small, 'Small'],
  [Breakpoints.Medium, 'Medium'],
  [Breakpoints.Large, 'Large'],
  [Breakpoints.XLarge, 'XLarge'],
]);

export const UiStore = signalStore(
  { providedIn: 'root' },
  withState(UiInitialStore),
  withMethods((
    state,
    themeService = inject(ThemeService),
    localStorageService = inject(LocalStorageFacade)
  ) => ({
    bodyClick: (payload: any) => patchState(state, { userClickEventTarget: payload }),
    toggleUiTheme: (mode: 'light' | 'dark' | 'system') => {
      let isDark: boolean;
      if (mode === 'system') {
        isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      } else {
        isDark = mode === 'dark';
      }

      themeService.setTheme(mode);
      localStorageService.setItem('theme', mode);
      patchState(state, (currentState) => ({
        ...currentState,
        theme: { mode, dark: isDark }
      }));
    },
    changeMaxWidth: (show) => patchState(state, { hideMaxWidth: show ?? !!state.hideMaxWidth() }),
    updateUiTheme: (mode: 'light' | 'dark' | 'system') => {
      themeService.setTheme(mode);
      const isDark = mode === 'system' 
        ? window.matchMedia('(prefers-color-scheme: dark)').matches 
        : mode === 'dark';
      patchState(state, { theme: { mode, dark: isDark } });
    },
    updateOpacityScreen: (payload: boolean) => patchState(state, { opacityScreen: payload }),
    setPrinting: (value: boolean) => patchState(state, { isPrinting: value }),
  })
  ),
  withHooks({
    onInit: (
      state,
      themeService = inject(ThemeService),
      localStorageService = inject(LocalStorageFacade),
      breakpointObserver = inject(BreakpointObserver),
      routerStore = inject(RouterStore),
      document = inject(DOCUMENT)
    ) => {
      let themeFromStore = localStorageService.getItem('theme') as 'light' | 'dark' | 'system' | null;
      if (!themeFromStore) {
        themeFromStore = 'system';
        localStorageService.setItem('theme', themeFromStore);
      }
            const isDark = themeFromStore === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
        : themeFromStore === 'dark';
      
      themeService.setTheme(themeFromStore);
      patchState(state, { theme: { mode: themeFromStore, dark: isDark } });

      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
        if (state.theme.mode() === 'system') {
          const isDarkNow = e.matches;
          themeService.setTheme('system');
          patchState(state, { theme: { mode: 'system', dark: isDarkNow } });
        }
      });
      breakpointObserver
        .observe([
          Breakpoints.XSmall,
          Breakpoints.Small,
          Breakpoints.Medium,
          Breakpoints.Large,
          Breakpoints.XLarge,
        ])
        .subscribe((result) => {
          if (state.isPrinting()) return;
          for (const query of Object.keys(result.breakpoints)) {
            if (result.breakpoints[query]) {
              patchState(state, {
                screen: {
                  ...state.screen(),
                  breakpoint: displayNameMap.get(query) ?? 'Unknown'
                }
              });
            }
          }
        }
        );
      breakpointObserver
        .observe(BELOW_SM_QUERY)
        .subscribe((result) => patchState(state, { isBelowTailwindSm: result.matches }));
      fromEvent(window, 'resize')
        .pipe(debounceTime(100))
        .subscribe((data: any) => {
          const height = JSON.parse(JSON.stringify(data?.target?.innerHeight));
          const width = JSON.parse(JSON.stringify(data?.target?.innerWidth));
          patchState(state, { screen: { ...state.screen(), innerHeight: height, innerWidth: width } }
          );
        }
        );
      fromEvent(window, 'orientationchange').subscribe(() => {
        window.matchMedia('(orientation: portrait)').matches
          ? patchState(state, { screen: { ...state.screen(), orientation: 'portrait' } })
          : patchState(state, { screen: { ...state.screen(), orientation: 'landscape' } });
      });
      patchState(state, { screen: { ...state.screen(), innerHeight: window?.innerHeight, innerWidth: window?.innerWidth } })
      effect(() => {
        const route = routerStore.getCurrentRoute();
        if (route) {
          if (route?.includes('profile')) {
            document.documentElement.style.setProperty(
              '--dx-theme-background-dark',
              '#171717'
            );
            document.documentElement.style.setProperty(
              '--dx-theme-background-light',
              '#f9fafb'
            );
            return;
          }
          document.documentElement.style.setProperty(
            '--dx-theme-background-light',
            '#ffffff'
          );
          document.documentElement.style.setProperty(
            '--dx-theme-background-dark',
            'rgb(30 33 36)'
          );
        }
      });
    }
  }),
  withComputed((state) => ({
    isMobile: () => {
      return state.screen().breakpoint === 'Small' || state.screen().breakpoint === 'XSmall';
    },
  }))
);
